import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { Github, MessageCircle, Search, FileText, ExternalLink, Repeat, ArrowRight, CornerUpRight } from 'lucide-react';
import type { ActivityItem, TwitterMediaItem } from '@/types';

interface ActivityFeedProps {
  items: ActivityItem[];
  isLoading: boolean;
}

const sourceIcons: Record<string, typeof Github> = {
  github: Github,
  twitter: MessageCircle,
  search: Search,
  blog: FileText,
};

const sourceColors: Record<string, string> = {
  github: 'from-gray-700 to-gray-800 dark:from-gray-700 dark:to-gray-800',
  twitter: 'from-sky-500 to-sky-600 dark:from-sky-600 dark:to-sky-700',
  search: 'from-amber-400 to-amber-500 dark:from-amber-500 dark:to-amber-600',
  blog: 'from-emerald-500 to-emerald-600 dark:from-emerald-600 dark:to-emerald-700',
};

const sourceLabels: Record<string, string> = {
  github: 'GitHub',
  twitter: 'X',
  search: 'Web Mention',
  blog: 'Blog Post',
};

export default function ActivityFeed({ items, isLoading }: ActivityFeedProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="bg-white dark:bg-slate-800/50 rounded-xl p-4 animate-pulse border border-slate-100 dark:border-transparent"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4" />
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-8 text-center border border-slate-200 dark:border-transparent">
        <p className="text-slate-500 dark:text-slate-400">No activity yet. The sync will run soon!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <motion.div
          key={item.id}
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="feed-item"
        >
          <ActivityCard item={item} />
        </motion.div>
      ))}
    </div>
  );
}

function ActivityCard({ item }: { item: ActivityItem }) {
  const Icon = sourceIcons[item.source] || FileText;
  const gradient = sourceColors[item.source] || 'from-slate-600 to-slate-700';
  const label = sourceLabels[item.source] || item.source;

  const timeAgo = formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });
  
  const metadata = item.metadata || {};
  const isRetweet = item.type === 'retweet' || metadata.isRetweet === true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isThread = (metadata as any).isSelfReply === true;
  const isReply = item.type === 'reply';

  // Normalize media: support both legacy string[] and new TwitterMediaItem[] formats
  const rawMedia = Array.isArray(metadata.media) ? metadata.media : [];
  const media: TwitterMediaItem[] = rawMedia.map((m) => {
    if (typeof m === 'string') return { url: m, type: 'image' as const };
    return m as TwitterMediaItem;
  }).filter(m => m.url);

  return (
    <article className="group bg-white/40 backdrop-blur-md dark:bg-slate-800/50 hover:bg-white/60 dark:hover:bg-slate-800/70 rounded-xl p-4 transition-all duration-200 border border-slate-200/60 dark:border-transparent hover:border-slate-300/80 dark:hover:border-slate-700/50 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none">
      <div className="flex items-start gap-4">
        {/* Source Icon */}
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 relative`}>
          <Icon className="w-5 h-5 text-white" />
          {isRetweet && (
            <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-900 rounded-full p-0.5 border border-slate-200 dark:border-slate-700">
              <Repeat className="w-3 h-3 text-slate-500 dark:text-slate-400" />
            </div>
          )}
          {!isRetweet && isThread && (
            <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-900 rounded-full p-0.5 border border-slate-200 dark:border-slate-700">
              <MessageCircle className="w-3 h-3 text-sky-500 dark:text-sky-400" />
            </div>
          )}
          {isReply && (
            <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-900 rounded-full p-0.5 border border-slate-200 dark:border-slate-700">
              <CornerUpRight className="w-3 h-3 text-violet-500 dark:text-violet-400" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {label}
            </span>
            {isRetweet && (
              <>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="text-xs text-secondary inline-flex items-center gap-1">
                  <Repeat className="w-3 h-3" /> Reposted
                </span>
              </>
            )}
            {isThread && (
              <>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="text-xs text-sky-600 dark:text-sky-400 inline-flex items-center gap-1 font-medium">
                  Thread
                </span>
              </>
            )}
            {isReply && (
              <>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="text-xs text-violet-600 dark:text-violet-400 inline-flex items-center gap-1 font-medium">
                  <CornerUpRight className="w-3 h-3" /> Reply
                </span>
              </>
            )}
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <time className="text-xs text-slate-500 dark:text-slate-500" dateTime={item.createdAt}>
              {timeAgo}
            </time>
          </div>

          {/* Title */}
          <h3 className="font-medium text-slate-900 dark:text-slate-200 mb-1 line-clamp-2">
            {item.title}
          </h3>

          {/* Content */}
          {item.content && (
            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap mb-2">
              {item.content}
            </p>
          )}

          {/* Reply context — show the parent tweet as a nested quote card */}
          {isReply && (() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ctx = (metadata as any).repliedToContext as { content: string; author?: string } | undefined;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const replyingTo = ((metadata as any).replyingTo as string | undefined)?.trim();

            if (!ctx?.content) {
              if (!replyingTo) return null;
              return (
                <p className="mt-1 mb-3 text-xs text-slate-500 dark:text-slate-400">
                  Replying to {replyingTo}
                </p>
              );
            }

            return (
              <blockquote className="mt-1 mb-3 pl-3 border-l-2 border-slate-300 dark:border-slate-600">
                {ctx.author && (
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-0.5 truncate">
                    {ctx.author}
                  </p>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-500 line-clamp-3 whitespace-pre-wrap">
                  {ctx.content}
                </p>
              </blockquote>
            );
          })()}
          
          {/* GitHub Metadata */}
          {/* eslint-disable @typescript-eslint/no-explicit-any */}
          {item.source === 'github' && (
            <div className="flex flex-wrap gap-2 mb-2">
              {/* Core Push Details */}
              {(metadata as any).ref && (
                 <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                   BRANCH: {(metadata as any).ref}
                 </span>
              )}
              
              {/* Stats for PRs */}
              {(metadata as any).stats && (
                <div className="flex items-center gap-2">
                  {(metadata as any).stats.additions !== undefined && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      +{(metadata as any).stats.additions}
                    </span>
                  )}
                  {(metadata as any).stats.deletions !== undefined && (
                    <span className="text-xs text-red-600 dark:text-red-400">
                      -{(metadata as any).stats.deletions}
                    </span>
                  )}
                  {(metadata as any).stats.changedFiles !== undefined && (
                    <span className="text-xs text-slate-500 dark:text-slate-500">
                      {(metadata as any).stats.changedFiles} file{(metadata as any).stats.changedFiles !== 1 && 's'}
                    </span>
                  )}
                </div>
              )}

              {/* Labels for Issues */}
              {(metadata as any).labels && Array.isArray((metadata as any).labels) && (
                 (metadata as any).labels.map((label: any, idx: number) => (
                   <span 
                     key={idx}
                     className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                     style={{ 
                       backgroundColor: `#${label.color}33`, 
                       color: `#${label.color}`,
                       border: `1px solid #${label.color}66`
                     }}
                   >
                     {label.name}
                   </span>
                 ))
              )}

              {/* PR/Issue State Badge */}
              {(metadata as any).state && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize border ${
                  (metadata as any).state === 'open' 
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700/50' 
                    : (metadata as any).merged 
                    ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-700/50'
                    : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-700/50'
                }`}>
                  {(metadata as any).merged ? 'merged' : (metadata as any).state}
                </span>
              )}
            </div>
          )}

          {/* Media Grid */}
          {media.length > 0 && (
            <div
              className={`mt-3 mb-3 grid gap-2 ${
                media.length > 1
                  ? 'grid-cols-1 md:grid-cols-2 w-full md:w-2/3'
                  : 'grid-cols-1 w-full md:w-1/2'
              }`}
            >
              {media.map((mediaItem, i) => {
                if (mediaItem.type === 'video') {
                  const tweetHref = item.url ?? '#';

                  // HLS streams (.m3u8) can't play natively — show a thumbnail + play overlay
                  if (mediaItem.url.includes('.m3u8')) {
                    const thumbSrc = mediaItem.thumbnail
                      ? /^https?:\/\//i.test(mediaItem.thumbnail)
                        ? `/api/proxy/image?url=${encodeURIComponent(mediaItem.thumbnail)}`
                        : mediaItem.thumbnail
                      : null;

                    return (
                      <div key={i} className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700/50 relative group/video">
                        {/* Thumbnail or dark fallback */}
                        {thumbSrc ? (
                          <img
                            src={thumbSrc}
                            alt="Video preview"
                            loading="lazy"
                            className="w-full h-auto block"
                          />
                        ) : (
                          <div className="w-full aspect-video bg-slate-900" />
                        )}
                        {/* Play overlay — links to the tweet */}
                        <a
                          href={tweetHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 group-hover/video:bg-black/55 transition-colors"
                          aria-label="Watch video on X"
                        >
                          <div className="w-14 h-14 rounded-full bg-black/60 border-2 border-white/80 flex items-center justify-center group-hover/video:scale-110 transition-transform shadow-lg">
                            <svg className="w-6 h-6 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="6,4 20,12 6,20" />
                            </svg>
                          </div>
                          <span className="text-white text-xs font-medium drop-shadow">Watch on X</span>
                        </a>
                      </div>
                    );
                  }

                  // Direct mp4/webm — proxy and render natively
                  const src = /^https?:\/\//i.test(mediaItem.url)
                    ? `/api/proxy/video?url=${encodeURIComponent(mediaItem.url)}`
                    : mediaItem.url;
                  return (
                    <div key={i} className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700/50 bg-slate-900">
                      <video
                        src={src}
                        poster={mediaItem.thumbnail
                          ? (/^https?:\/\//i.test(mediaItem.thumbnail)
                            ? `/api/proxy/image?url=${encodeURIComponent(mediaItem.thumbnail)}`
                            : mediaItem.thumbnail)
                          : undefined}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls
                        className="w-full h-auto max-h-64 object-contain"
                      />
                    </div>
                  );
                }

                // Images: proxy through /api/proxy/image
                const src = /^https?:\/\//i.test(mediaItem.url)
                  ? `/api/proxy/image?url=${encodeURIComponent(mediaItem.url)}`
                  : mediaItem.url;

                return (
                  <div key={i} className="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-900/50">
                    <img
                      src={src}
                      alt="Post media"
                      data-original-src={mediaItem.url}
                      loading="lazy"
                      onError={(event) => {
                        const img = event.currentTarget;
                        const original = img.dataset.originalSrc;
                        if (original && img.src !== original) {
                          img.src = original;
                          return;
                        }
                        if (original?.includes('pbs.twimg.com/media/') && original.includes('name=large')) {
                          img.src = original.replace('name=large', 'name=medium');
                        }
                      }}
                      className="w-full h-auto hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Link */}
          {item.url && (
            item.source === 'blog' ? (
              <Link
                to={item.url}
                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
              >
                Read Article
                <ArrowRight className="w-3 h-3" />
              </Link>
            ) : (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
              >
                View on {label}
                <ExternalLink className="w-3 h-3" />
              </a>
            )
          )}
        </div>
      </div>
    </article>
  );
}
